// /home/Hakyury/sites/eight-percent.com/frontend/src/utils/authFetch.js
let refreshPromise = null;
let refreshTimeoutId = null;

export const REFRESH_ENDPOINT = 'https://oauth.eight-percent.com/api/accounts/token/refresh/';
const SKEW_SECONDS = 45;

// Ключи для хранения токенов
const TOKEN_KEYS = {
  ACCESS: 'eight_percent_access_token',
  REFRESH: 'eight_percent_refresh_token'
};

// Синхронизированное хранение токенов
export function getTokens() {
  return {
    access: localStorage.getItem(TOKEN_KEYS.ACCESS),
    refresh: localStorage.getItem(TOKEN_KEYS.REFRESH)
  };
}

export function setTokens({ access, refresh }) {
  if (access) {
    localStorage.setItem(TOKEN_KEYS.ACCESS, access);
  }
  
  if (refresh) {
    localStorage.setItem(TOKEN_KEYS.REFRESH, refresh);
  }
  
  scheduleProactiveRefresh();
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEYS.ACCESS);
  localStorage.removeItem(TOKEN_KEYS.REFRESH);
  clearTimeout(refreshTimeoutId);
}

function decodeJwt(token) {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    return JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
  } catch {
    return null;
  }
}

function getExp(token) {
  const p = decodeJwt(token);
  return p?.exp || 0;
}

function isExpiring(token, skew = SKEW_SECONDS) {
  if (!token) return true;
  const exp = getExp(token);
  if (!exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return exp - now <= skew;
}

function scheduleProactiveRefresh(refreshEndpoint = REFRESH_ENDPOINT) {
  clearTimeout(refreshTimeoutId);
  const { access } = getTokens();
  const exp = getExp(access);
  if (!exp) return;
  const nowMs = Date.now();
  const delayMs = Math.max(exp * 1000 - nowMs - SKEW_SECONDS * 1000, 0);
  if (delayMs > 0) {
    refreshTimeoutId = setTimeout(() => {
      void ensureFreshAccess(refreshEndpoint).catch(() => {});
    }, delayMs);
  }
}

async function doRefresh(refreshEndpoint = REFRESH_ENDPOINT) {
  const { refresh } = getTokens();
  if (!refresh) throw new Error('no_refresh_token');

  const resp = await fetch(refreshEndpoint, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      'Accept': 'application/json'
    },
    body: JSON.stringify({ refresh })
  });
  
  if (!resp.ok) throw new Error('refresh_failed');

  const data = await resp.json();
  if (!data.access) throw new Error('no_access_in_response');

  setTokens({ access: data.access, refresh: data.refresh });
  return data.access;
}

async function ensureFreshAccess(refreshEndpoint = REFRESH_ENDPOINT) {
  if (!refreshPromise) {
    refreshPromise = doRefresh(refreshEndpoint)
      .catch((err) => {
        clearTokens();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Проверка авторизации
export function isAuthenticated() {
  const { access } = getTokens();
  return !!access && !isExpiring(access, 0);
}

// Получение информации о пользователе из токена
export function getUserInfo() {
  const { access } = getTokens();
  if (!access) return null;
  
  const payload = decodeJwt(access);
  if (!payload) return null;
  
  return {
    username: payload.login || payload.username,
    role: payload.role,
    userId: payload.user_id || payload.profile_id,
    email_verified: payload.email_verified
  };
}

// Проверка роли администратора
export function isAdmin() {
  const user = getUserInfo();
  return user?.role === 'Администратор';
}

// Инициализация таймеров при запуске приложения
export function initAuthTimers(refreshEndpoint = REFRESH_ENDPOINT) {
  const { access } = getTokens();
  if (isExpiring(access)) {
    ensureFreshAccess(refreshEndpoint).catch(() => {});
  } else {
    scheduleProactiveRefresh(refreshEndpoint);
  }
}

// Извлечение токенов из URL
export function extractTokensFromURL() {
  const url = new URL(window.location.href);
  const accessToken = url.searchParams.get('access_token');
  const refreshToken = url.searchParams.get('refresh_token');
  
  if (accessToken && refreshToken) {
    setTokens({ access: accessToken, refresh: refreshToken });
    
    // Удаляем токены из URL
    url.searchParams.delete('access_token');
    url.searchParams.delete('refresh_token');
    const newUrl = url.toString();
    window.history.replaceState({}, '', newUrl);
    
    return true;
  }
  
  return false;
}

// Функция для редиректа на OAuth
export function getOAuthRedirectUrl() {
  const currentUrl = window.location.href;
  return `https://oauth.eight-percent.com?next=${encodeURIComponent(currentUrl)}`;
}

// Выход выполняется через OAuth: localStorage изолирован по доменам, поэтому
// основной сайт не может сам очистить токены, сохранённые на OAuth-домене.
export function getOAuthLogoutUrl(returnUrl = 'https://eight-percent.com/') {
  const url = new URL('https://oauth.eight-percent.com/');
  url.searchParams.set('logout', '1');
  url.searchParams.set('next', returnUrl);
  return url.toString();
}
